import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  getFirestore,
  collection,
  getDocs,
  getDoc,
  doc,
  addDoc,
  deleteDoc,
  Timestamp,
} from "firebase/firestore";
import FavouriteButton from "./FavouriteButton";
import { useAuth } from "../firebase/AuthContext";

const MANUAL_REASONS = [
  "Physically checked - not found",
  "Booking out missed",
  "Duplicate warehouse record",
  "Stocktake correction",
  "Other",
];

const WarehouseAgedBookOut = () => {
  const [cylinders, setCylinders] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [bookingOutId, setBookingOutId] = useState("");
  const [status, setStatus] = useState("");
  const [selectedCylinder, setSelectedCylinder] = useState(null);
  const [manualReason, setManualReason] = useState(MANUAL_REASONS[0]);
  const [manualNotes, setManualNotes] = useState("");
  const firestore = getFirestore();
  const { currentUser, profile } = useAuth();

  const getAgeInDays = (timestamp) => {
    if (!timestamp?.seconds) return null;
    const bookedInMs = timestamp.seconds * 1000;
    const ageMs = Date.now() - bookedInMs;
    return Math.floor(ageMs / (1000 * 60 * 60 * 24));
  };

  const getProductCodeMap = useCallback(async (productIds) => {
    const uniqueIds = Array.from(new Set(productIds.filter(Boolean)));
    const entries = await Promise.all(
      uniqueIds.map(async (productId) => {
        try {
          const infoSnap = await getDoc(doc(firestore, "cyl_info", productId));
          if (!infoSnap.exists()) return [productId, "Not Found"];
          return [productId, infoSnap.data().productCode || "Unknown"];
        } catch {
          return [productId, "Unknown"];
        }
      })
    );

    return Object.fromEntries(entries);
  }, [firestore]);

  const fetchWarehouseCylinders = useCallback(async () => {
    setLoading(true);
    setStatus("");

    try {
      const warehouseSnap = await getDocs(collection(firestore, "cylinders_warehouse"));
      const rows = warehouseSnap.docs.map((docSnap) => {
        const data = docSnap.data();

        return {
          id: docSnap.id,
          ProductId: data.ProductId || "",
          SerialNumber: data.SerialNumber || "",
          LocationId: data.LocationId || "",
          BookedInTime: data.BookedInTime || null,
          AgeInDays: getAgeInDays(data.BookedInTime),
          Stock: data.Stock ? "STOCK" : "ORDER",
          Allocated: Boolean(data.Allocated),
          FullData: data,
        };
      });

      const productCodeMap = await getProductCodeMap(rows.map((row) => row.ProductId));
      const withCodes = rows
        .map((row) => ({
          ...row,
          ProductCode: row.ProductId ? productCodeMap[row.ProductId] || "Unknown" : "Unknown",
        }))
        .sort((a, b) => (b.AgeInDays ?? -1) - (a.AgeInDays ?? -1));

      setCylinders(withCodes);
    } catch (error) {
      console.error("Error loading warehouse cylinders:", error);
      setStatus("Could not load warehouse cylinders. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [firestore, getProductCodeMap]);

  useEffect(() => {
    fetchWarehouseCylinders();
  }, [fetchWarehouseCylinders]);

  const openManualBookOut = (cylinder) => {
    setSelectedCylinder(cylinder);
    setManualReason(MANUAL_REASONS[0]);
    setManualNotes("");
    setStatus("");
  };

  const closeManualBookOut = () => {
    setSelectedCylinder(null);
    setManualReason(MANUAL_REASONS[0]);
    setManualNotes("");
  };

  const handleManualBookOut = async () => {
    if (!selectedCylinder) return;

    const notes = manualNotes.trim();
    if ((manualReason === "Other" || manualReason === "Stocktake correction") && !notes) {
      setStatus("Please add notes for this manual book-out reason.");
      return;
    }

    setBookingOutId(selectedCylinder.id);
    setStatus("");

    const now = Timestamp.now();
    const userName =
      currentUser?.displayName ||
      profile?.displayName ||
      profile?.name ||
      currentUser?.email ||
      "Unknown user";
    const userEmail = currentUser?.email || profile?.email || "";

    try {
      await addDoc(collection(firestore, "warehouse_archive"), {
        ...selectedCylinder.FullData,
        BookedOutTime: now,
        BookedOutMethod: "manual_warehouse_book_out",
        ManualBookOut: true,
        ManualBookOutReason: manualReason,
        ManualBookOutNotes: notes,
        ManualBookOutTime: now,
        ManualBookOutByUid: currentUser?.uid || "",
        ManualBookOutByEmail: userEmail,
        ManualBookOutByName: userName,
        ManualBookOutSourceCollection: "cylinders_warehouse",
        ManualBookOutSourceDocumentId: selectedCylinder.id,
        ManualBookOutProductCode: selectedCylinder.ProductCode,
        ManualBookOutSearchText: [
          "manual",
          "manual_warehouse_book_out",
          manualReason,
          notes,
          selectedCylinder.SerialNumber,
          selectedCylinder.ProductId,
          selectedCylinder.ProductCode,
          selectedCylinder.LocationId,
          userEmail,
          userName,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase(),
      });
      await deleteDoc(doc(firestore, "cylinders_warehouse", selectedCylinder.id));

      setCylinders((current) => current.filter((row) => row.id !== selectedCylinder.id));
      setStatus(`Manually booked out serial ${selectedCylinder.SerialNumber}.`);
      closeManualBookOut();
    } catch (error) {
      console.error("Error manually booking out cylinder:", error);
      setStatus("Could not manually book out cylinder. Please try again.");
    } finally {
      setBookingOutId("");
    }
  };

  const formatDate = (timestamp) =>
    timestamp?.seconds ? new Date(timestamp.seconds * 1000).toLocaleString() : "N/A";

  const filteredCylinders = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return cylinders;

    return cylinders.filter((cylinder) =>
      [
        cylinder.SerialNumber,
        cylinder.ProductCode,
        cylinder.ProductId,
        cylinder.LocationId,
        cylinder.Stock,
        cylinder.Allocated ? "allocated" : "not allocated",
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query))
    );
  }, [cylinders, searchQuery]);

  return (
    <>
      <FavouriteButton pageUrl={window.location.pathname} />
      <div className="p-6 max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-semibold text-gray-800">Manual Warehouse Book Out</h1>
            <p className="text-gray-600 mt-1">
              Search live warehouse stock and manually archive one cylinder at a time.
            </p>
          </div>
          <button
            onClick={fetchWarehouseCylinders}
            disabled={loading}
            className="px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-400"
          >
            {loading ? "Refreshing..." : "Refresh"}
          </button>
        </div>

        <div className="flex flex-col md:flex-row md:items-center gap-4 mb-4">
          <input
            type="text"
            placeholder="Search by serial, product, location, stock or allocated..."
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            className="w-full md:w-1/2 p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none"
          />
          <div className="text-sm text-gray-600">
            Showing {filteredCylinders.length} of {cylinders.length}
          </div>
        </div>

        {status && (
          <div className="mb-4 rounded-md border border-gray-200 bg-gray-50 px-4 py-2 text-gray-700">
            {status}
          </div>
        )}

        <div className="overflow-x-auto border border-gray-200 rounded-md bg-white">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-2 text-left">Serial Number</th>
                <th className="px-4 py-2 text-left">Product Code</th>
                <th className="px-4 py-2 text-left">Location</th>
                <th className="px-4 py-2 text-left">Stock</th>
                <th className="px-4 py-2 text-left">Allocated</th>
                <th className="px-4 py-2 text-left">Booked In</th>
                <th className="px-4 py-2 text-left">Age</th>
                <th className="px-4 py-2 text-left">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan="8" className="text-center py-6 text-gray-500">
                    Loading warehouse cylinders...
                  </td>
                </tr>
              )}

              {!loading &&
                filteredCylinders.map((cylinder) => (
                  <tr key={cylinder.id} className="border-t border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-2">{cylinder.SerialNumber}</td>
                    <td className="px-4 py-2">{cylinder.ProductCode}</td>
                    <td className="px-4 py-2">{cylinder.LocationId}</td>
                    <td className="px-4 py-2">{cylinder.Stock}</td>
                    <td className="px-4 py-2">{cylinder.Allocated ? "Yes" : "No"}</td>
                    <td className="px-4 py-2">{formatDate(cylinder.BookedInTime)}</td>
                    <td className="px-4 py-2 font-medium">
                      {cylinder.AgeInDays === null ? "N/A" : `${cylinder.AgeInDays} days`}
                    </td>
                    <td className="px-4 py-2">
                      <button
                        onClick={() => openManualBookOut(cylinder)}
                        disabled={Boolean(bookingOutId)}
                        className="px-3 py-1 rounded-md bg-red-600 text-white hover:bg-red-700 disabled:bg-gray-400"
                      >
                        Manual Book Out
                      </button>
                    </td>
                  </tr>
                ))}

              {!loading && filteredCylinders.length === 0 && (
                <tr>
                  <td colSpan="8" className="text-center py-6 text-gray-500">
                    No warehouse cylinders found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {selectedCylinder && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">
              Confirm Manual Book Out
            </h2>

            <div className="grid grid-cols-2 gap-3 text-sm mb-4">
              <div>
                <div className="text-gray-500">Serial</div>
                <div className="font-medium">{selectedCylinder.SerialNumber}</div>
              </div>
              <div>
                <div className="text-gray-500">Product</div>
                <div className="font-medium">{selectedCylinder.ProductCode}</div>
              </div>
              <div>
                <div className="text-gray-500">Location</div>
                <div className="font-medium">{selectedCylinder.LocationId || "N/A"}</div>
              </div>
              <div>
                <div className="text-gray-500">Booked In</div>
                <div className="font-medium">{formatDate(selectedCylinder.BookedInTime)}</div>
              </div>
            </div>

            <label className="block text-gray-700 font-medium mb-1" htmlFor="manualReason">
              Reason
            </label>
            <select
              id="manualReason"
              value={manualReason}
              onChange={(event) => setManualReason(event.target.value)}
              className="w-full p-2 border rounded-md mb-4 focus:ring-2 focus:ring-blue-500 focus:outline-none"
            >
              {MANUAL_REASONS.map((reason) => (
                <option key={reason} value={reason}>
                  {reason}
                </option>
              ))}
            </select>

            <label className="block text-gray-700 font-medium mb-1" htmlFor="manualNotes">
              Notes
            </label>
            <textarea
              id="manualNotes"
              value={manualNotes}
              onChange={(event) => setManualNotes(event.target.value)}
              rows="4"
              placeholder="Add useful detail for audit/search..."
              className="w-full p-2 border rounded-md mb-5 focus:ring-2 focus:ring-blue-500 focus:outline-none"
            />

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={closeManualBookOut}
                disabled={bookingOutId === selectedCylinder.id}
                className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:bg-gray-100"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleManualBookOut}
                disabled={bookingOutId === selectedCylinder.id}
                className="px-4 py-2 rounded-md bg-red-600 text-white hover:bg-red-700 disabled:bg-gray-400"
              >
                {bookingOutId === selectedCylinder.id ? "Booking Out..." : "Confirm Manual Book Out"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default WarehouseAgedBookOut;
